import React, { useEffect, useState, useRef, useCallback } from "react";
import Stack from '@mui/material/Stack';
import Controls from './Controls';
import Typography from '@mui/material/Typography';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js'
import WaveSurfer from 'wavesurfer.js'
import Zoom from '@mui/material/Zoom';
import { ButtonBase, IconButton, Menu, MenuItem } from "@mui/material";
import Lottie from 'react-lottie';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import animationRecording from '../images/animation_recording.json';
import MicIcon from '@mui/icons-material/Mic';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AccountIcon from "../Account/AccountIcon";
import { useTranslation } from 'react-i18next';
import {
  ACCENT, ACCENT_DARK, ACCENT_GLOW, TEXT_PRIMARY, TEXT_SECONDARY,
  glassCard, noMotion, TYPE_SCALE
} from '../designsystem/tokens';
import RealtimeTranscription from '../Services/RealtimeTranscription';
import { getLanguage } from '../Services/i18n';

const Recorder = ({ animation = true, clean, finished, onClear, onStart, small, onStopRecording, audioText, example, hint, onTranscript, enableLiveTranscription = false }) => {
  const { t } = useTranslation();
  const [audioStateText, setAudioStateText] = useState(audioText || t('audioText'));
  const [progress, setProgress] = useState("00:00");
  const [mic, setMic] = useState(null);
  const [inputs, setInputs] = useState([]);
  const waveformRef = useRef(null);
  const [record, setRecord] = useState(null);
  const [wavesurfer, setWavesurfer] = useState(null);
  const [start, setStart] = useState(false);
  const [recording, setRecording] = useState(false)
  const [recordingAnim, setRecordingAnim] = useState(false)
  const [clear, setClear] = useState(false)
  const cancelled = useRef(false)
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const [logoScale, setLogoScale] = useState(1);
  const [micMenuAnchor, setMicMenuAnchor] = useState(null);
  const micMenuOpen = Boolean(micMenuAnchor);
  const pauseFabRef = useRef(null);
  const rtTranscriptionRef = useRef(null);
  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const recordingAnimation = {
    loop: true,
    autoplay: true,
    animationData: animationRecording,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice"
    }
  };

  let scrollingWaveform = true

  useEffect(() => {
    RecordPlugin.getAvailableAudioDevices().then((devices) => {
      var deviceInputs = []
      devices.forEach((device) => {
        deviceInputs.push({
          value: device.deviceId,
          label: device.label || device.deviceId,
        })
      })
      setInputs(deviceInputs)
      if (deviceInputs.length > 0) {
        setMic(deviceInputs[0].value)
      }
    })
    return () => {
      if (wavesurfer != null) {
        wavesurfer.destroy()
      }
      stopAudioLevelMonitoring();
    };
  }, []);

  // Focus pause FAB when recording starts
  useEffect(() => {
    if (start && pauseFabRef.current) {
      pauseFabRef.current.focus();
    }
  }, [start]);

  // Audio level monitoring for logo breathing effect
  const startAudioLevelMonitoring = useCallback((stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = { analyser, audioContext };

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
        const normalizedLevel = Math.min(avg / 128, 1);
        if (prefersReducedMotion) {
          setLogoScale(1);
        } else {
          setLogoScale(1 + normalizedLevel * 0.15);
        }
        animFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch (e) {
      console.warn('Audio level monitoring not available:', e);
    }
  }, [prefersReducedMotion]);

  const stopAudioLevelMonitoring = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.audioContext.close().catch(() => {});
      analyserRef.current = null;
    }
    setLogoScale(1);
  }, []);

  function createWaveSurfer() {
    if (wavesurfer != null) {
      wavesurfer.destroy()
    }

    let ws = WaveSurfer.create({
      container: waveformRef.current,
      height: 124,
      normalize: false,
      waveColor: ACCENT_DARK,
      progressColor: ACCENT_DARK,
      cursorColor: ACCENT_DARK,
      cursorWidth: 0,
      barWidth: null,
      barGap: null,
      barRadius: null,
      barHeight: null,
      barAlign: "center",
      minPxPerSec: 1,
      fillParent: true,
      mediaControls: false,
      autoplay: false,
      interact: false,
      dragToSeek: false,
      hideScrollbar: false,
      audioRate: 1,
      autoScroll: true,
      autoCenter: true,
    })

    let recordPlugin = ws.registerPlugin(RecordPlugin.create({ scrollingWaveform, renderRecordedAudio: true }))
    recordPlugin.on('record-end', (blob) => {
      if (!cancelled.current) {
        stopAudioLevelMonitoring();
        setRecording(false)
        setRecordingAnim(false)
        setStart(false)
        finished(blob)
      }
    })

    recordPlugin.on('record-progress', (time) => {
      updateProgress(time)
    })
    setRecord(recordPlugin)
    setWavesurfer(ws)
    return recordPlugin
  }

  function updateProgress(time) {
    const formattedTime = [
      Math.floor((time % 3600000) / 60000),
      Math.floor((time % 60000) / 1000),
    ]
      .map((v) => (v < 10 ? '0' + v : v))
      .join(':')
    setProgress(formattedTime)
  }

  function startRecord() {
    onStart()
    cancelled.current = false
    setClear(false)
    setRecording(true)
    setRecordingAnim(true)
    let record = createWaveSurfer()
    setStart(true)
    if (record.isRecording() || record.isPaused()) {
      record.stopRecording()
      return
    }
    const deviceId = mic
    record.startRecording({ deviceId }).then(() => {
      setAudioStateText(t('recording'));
      // Start audio level monitoring
      if (record.stream) {
        startAudioLevelMonitoring(record.stream);
        // Start live transcription if enabled
        if (enableLiveTranscription && onTranscript) {
          rtTranscriptionRef.current = new RealtimeTranscription({
            onTranscript: (text, confirmed) => onTranscript(text, confirmed),
            language: getLanguage(),
          });
          rtTranscriptionRef.current.start(record.stream);
        }
      }
    })
  }

  function pauseRecord() {
    if (record.isPaused()) {
      setRecording(true)
      setRecordingAnim(true)
      setAudioStateText(t('recording'));
      record.resumeRecording()
    } else {
      setRecording(false)
      setRecordingAnim(false)
      setAudioStateText(t('paused'));
      record.pauseRecording()
    }
  }

  function stopRecording() {
    stopAudioLevelMonitoring();
    if (rtTranscriptionRef.current) { rtTranscriptionRef.current.stop(); rtTranscriptionRef.current = null; }
    setRecordingAnim(false)
    setRecording(false)
    record.stopRecording()
    setAudioStateText(t('processingAudio'));
  }

  function clearRecording() {
    cancelled.current = true
    stopAudioLevelMonitoring();
    if (rtTranscriptionRef.current) { rtTranscriptionRef.current.stop(); rtTranscriptionRef.current = null; }
    setRecording(false)
    setRecordingAnim(false)
    setStart(false)
    onClear()
    setClear(true)
    setAudioStateText(audioText || t('audioText'));
    record.stopRecording()
    if (onStopRecording) {
      onStopRecording()
    }
  }

  const handleMicSelect = (deviceId) => {
    setMic(deviceId);
    setMicMenuAnchor(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!start) startRecord();
    }
  };

  return (
    <Stack>
      <Stack spacing={2}>
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}>
          {/* Example text display */}
          {example && (
            <Typography
              variant="body1"
              sx={{
                mt: 1,
                color: TEXT_PRIMARY,
                maxWidth: '100%',
                textAlign: 'left',
                ...glassCard,
                whiteSpace: 'pre-line',
              }}
            >
              {example}
            </Typography>
          )}

          {/* Hint text from suggestion chips */}
          {hint && !start && (
            <Typography
              data-testid="recorder-hint"
              variant="body2"
              sx={{
                color: TEXT_SECONDARY,
                textAlign: 'center',
                mb: 1,
                maxWidth: '80%',
                fontSize: '0.82rem',
                fontStyle: 'italic',
                transition: 'opacity 300ms ease-out',
                ...noMotion,
              }}
            >
              {hint}
            </Typography>
          )}

          {/* Status text */}
          <Typography
            sx={{
              zIndex: 1,
              mb: 2,
              color: TEXT_PRIMARY,
            }}
            variant="h5"
            align="center"
          >
            {audioStateText}
          </Typography>

          {/* Logo + Lottie overlay container using CSS Grid */}
          <Box sx={{
            display: 'grid',
            placeItems: 'center',
            width: '100%',
          }}>
            {/* Lottie animation behind the logo */}
            {animation && recordingAnim && (
              <Box sx={{ gridRow: 1, gridColumn: 1, zIndex: 0 }}>
                <Lottie
                  options={recordingAnimation}
                  height="325px"
                  width="325px"
                  isPaused={prefersReducedMotion}
                  style={{ transition: "all 1s ease-in-out" }}
                />
              </Box>
            )}

            {/* Record button with pulse ring */}
            <ButtonBase
              data-testid="record-button"
              aria-label={t('audioText')}
              tabIndex={0}
              onKeyDown={handleKeyDown}
              onClick={() => { if (!start) startRecord() }}
              sx={{
                gridRow: 1,
                gridColumn: 1,
                zIndex: 1,
                borderRadius: '50%',
                animation: !start && !prefersReducedMotion ? 'pulseRing 2s ease-out infinite' : 'none',
                transform: `scale(${logoScale})`,
                transition: prefersReducedMotion ? 'none' : 'transform 0.1s ease-out',
                ...noMotion,
              }}
            >
              <img
                src="/button_Labia.svg"
                alt="Labia"
                style={{ width: 170, height: 172 }}
              />
            </ButtonBase>
          </Box>

          {/* Mic selector — Menu instead of Dialog */}
          {!start && inputs.length > 1 &&
            <Box sx={{ mt: 2 }}>
              <IconButton
                data-testid="mic-selector"
                aria-label={t('selectMicrophone')}
                aria-controls={micMenuOpen ? 'mic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={micMenuOpen ? 'true' : undefined}
                onClick={(e) => setMicMenuAnchor(e.currentTarget)}
                sx={{
                  borderRadius: '30px',
                  color: ACCENT_DARK,
                  backgroundColor: 'rgba(255,255,255,0.74)',
                  px: 2,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                }}
              >
                <MicIcon />
                <ArrowDropDownIcon />
              </IconButton>
              <Menu
                id="mic-menu"
                anchorEl={micMenuAnchor}
                open={micMenuOpen}
                onClose={() => setMicMenuAnchor(null)}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: '#0f1b2e',
                      backgroundImage: 'none',
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: TEXT_PRIMARY,
                    }
                  }
                }}
              >
                {inputs.map((input) => (
                  <MenuItem
                    key={input.value}
                    selected={mic === input.value}
                    onClick={() => handleMicSelect(input.value)}
                  >
                    {input.label}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          }

        </Box>

        {/* Hidden waveform container */}
        <div ref={waveformRef} style={{ display: "none" }} />

        {/* Recording controls */}
        {start && (
          <Zoom in={start} style={{ width: "100%" }}>
            <Stack
              spacing={2}
              justifyContent="space-around"
              alignItems="center"
              sx={{
                width: "100%",
                color: TEXT_PRIMARY,
                position: small ? "relative" : "absolute",
                bottom: small ? "0%" : "5%",
              }}
            >
              <Typography
                data-testid="recording-timer"
                sx={{
                  fontSize: TYPE_SCALE.xl,
                  fontVariantNumeric: 'tabular-nums',
                  color: TEXT_SECONDARY,
                }}
              >
                {progress}
              </Typography>
              <Box ref={pauseFabRef} tabIndex={-1} sx={{ width: '100%', outline: 'none' }}>
                <Controls
                  onClear={clearRecording}
                  onPause={pauseRecord}
                  onFinish={stopRecording}
                  recording={recording}
                  recordInProgress={start}
                />
              </Box>
            </Stack>
          </Zoom>
        )}

        {/* Recording cancelled alert */}
        <Zoom in={clear} style={{ transitionDelay: '500ms' }}>
          <Alert
            onClose={() => setClear(false)}
            severity="error"
            sx={{
              position: "absolute",
              top: "max(env(safe-area-inset-top, 0px), 5%)",
              width: "90%",
              left: "5%",
              zIndex: 1000,
              borderRadius: '12px',
            }}
          >
            {t('recordingCancelled')}
          </Alert>
        </Zoom>
      </Stack>
    </Stack>
  )
};

export default Recorder;
